import { useEffect, useMemo, useRef, useState } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { ColumnLayout } from "@/libraries/design/foundations";
import {
    EnrollmentType,
    useExperiment
} from "@/utils/experiment/use-experiment";
import { wacRefreshQ4Fy26Experiment } from "@/utils/experiment/wacRefreshExperiment";

export interface Logo {
    src: string;
    alt: string;
    width?: number;
}

export interface LogosProps {
    title?: string;
    tagType?: "h2" | "h3" | "h4";
    logos: Logo[];
}

/**
 * Computes a max-width for the logos container that forces flex-wrap to
 * distribute logos into visually balanced rows (even pixel widths, same row count).
 *
 * Strategy:
 * 1. Wait for images to load, then measure actual rendered <li> widths and gap.
 * 2. Observe a wrapper <div> width via ResizeObserver (separate from the <ul>
 *    to avoid feedback loops from applying maxWidth to the <ul> itself).
 * 3. Evaluate every possible row-split budget and pick the one that minimises
 *    the standard deviation of row pixel widths.
 */
function useBalancedMaxWidth(logoCount: number): {
    maxWidth: string | undefined;
    wrapperRef: { current: HTMLDivElement | null };
    listRef: { current: HTMLUListElement | null };
} {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [measured, setMeasured] = useState<{ widths: number[]; gap: number }>(
        { widths: [], gap: 0 }
    );

    // Observe the wrapper div (not the <ul>) to avoid a ResizeObserver feedback
    // loop — applying maxWidth to the <ul> doesn't affect the wrapper's width.
    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            if (entry) setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Measure actual rendered <li> widths and the gap between them.
    // Runs when logoCount or containerWidth changes. On each measurement we
    // temporarily clear maxWidth so we always get the natural unconstrained
    // widths — this keeps the simulation in sync with the actual browser layout.
    useEffect(() => {
        const ul = listRef.current;
        if (!ul) return;

        const measure = () => {
            const prevMaxWidth = ul.style.maxWidth;
            const prevMargin = ul.style.margin;
            ul.style.maxWidth = "none";
            ul.style.margin = "0";

            const items = Array.from(ul.children) as HTMLElement[];
            const widths = items.map((el) => el.getBoundingClientRect().width);

            // Measure gap from actual DOM positions of adjacent items on the same row
            let gap = 0;
            const r0 = items[0]?.getBoundingClientRect();
            const r1 = items[1]?.getBoundingClientRect();
            if (r0 && r1 && r1.top === r0.top) {
                gap = r1.left - r0.right;
            }

            ul.style.maxWidth = prevMaxWidth;
            ul.style.margin = prevMargin;

            setMeasured({ widths, gap });
        };

        // On first mount / logo changes, wait for images to load before measuring.
        // On container resize, images are already loaded so measure directly.
        let cancelled = false;
        const images = Array.from(ul.querySelectorAll("img"));
        const unloaded = images.filter((img) => !img.complete);
        if (unloaded.length === 0) {
            measure();
        } else {
            Promise.all(
                unloaded.map(
                    (img) =>
                        new Promise<void>((res) =>
                            img.addEventListener("load", () => res(), {
                                once: true
                            })
                        )
                )
            ).then(() => {
                if (!cancelled) measure();
            });
        }
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoCount, containerWidth]);

    const maxWidth = useMemo(() => {
        const { widths, gap } = measured;
        if (!containerWidth || widths.length === 0) return undefined;

        const totalWidth =
            widths.reduce((sum, w) => sum + w, 0) + gap * (widths.length - 1);

        // Everything fits on one row — no constraint needed
        if (totalWidth <= containerWidth) return undefined;

        // Greedily split logos into rows given a max row pixel width budget.
        // Returns the pixel width of each row.
        const splitIntoRowWidths = (budget: number): number[] => {
            const rowWidths: number[] = [];
            let rowWidth = 0;
            for (const w of widths) {
                const needed = rowWidth === 0 ? w : gap + w;
                if (rowWidth > 0 && rowWidth + needed > budget) {
                    rowWidths.push(rowWidth);
                    rowWidth = w;
                } else {
                    rowWidth += needed;
                }
            }
            rowWidths.push(rowWidth);
            return rowWidths;
        };

        const currentRowWidths = splitIntoRowWidths(containerWidth);
        const numRows = currentRowWidths.length;
        if (numRows <= 1) return undefined;

        // Score by the minimum row width — maximising it ensures no row is
        // disproportionately short.
        const score = (rowWidths: number[]) => Math.min(...rowWidths);

        // Candidate budgets: pixel width of every possible contiguous subsequence
        // of logos. These are the only meaningful flex-wrap split points.
        const candidateBudgets = new Set<number>();
        for (let start = 0; start < widths.length; start++) {
            let rowW = 0;
            for (let end = start; end < widths.length; end++) {
                rowW += (widths[end] ?? 0) + (end > start ? gap : 0);
                candidateBudgets.add(rowW);
            }
        }

        let best: string | undefined;
        let bestScore = score(currentRowWidths);

        for (const budget of candidateBudgets) {
            if (budget >= containerWidth) continue;
            const rowWidths = splitIntoRowWidths(budget);
            if (rowWidths.length !== numRows) continue;
            const s = score(rowWidths);
            if (s > bestScore) {
                bestScore = s;
                best = `${budget}px`;
            }
        }

        return best;
    }, [measured, containerWidth]);

    return { maxWidth, wrapperRef, listRef };
}

export const Logos = ({ title, tagType, logos }: LogosProps) => {
    const checkWacRefresh = useExperiment(wacRefreshQ4Fy26Experiment);
    const isWacRefresh =
        checkWacRefresh.type === EnrollmentType.ENROLLED &&
        checkWacRefresh.value;

    const logoStyles = {
        height: "32px",
        width: "auto",
        maxWidth: "auto",
        filter: isWacRefresh // css color filter generator for color.neutral.light900
            ? "brightness(0) saturate(100%) invert(20%) sepia(5%) saturate(755%) hue-rotate(185deg) brightness(98%) contrast(86%)"
            : "grayscale(100%) brightness(66%)",
        ["-webkit-filter"]: isWacRefresh
            ? "brightness(0) saturate(100%) invert(20%) sepia(5%) saturate(755%) hue-rotate(185deg) brightness(98%) contrast(86%)"
            : "grayscale(100%) brightness(66%)"
    };

    const { maxWidth, wrapperRef, listRef } = useBalancedMaxWidth(logos.length);

    const logosComponent = (
        <div ref={wrapperRef} css={logosWrapperStyle}>
            <ul
                ref={listRef}
                css={[
                    logosContainerStyle,
                    isWacRefresh && wacRefreshContainerStyles,
                    isWacRefresh &&
                        title &&
                        wacRefreshLogosContainerWithTitleStyles
                ]}
                style={maxWidth ? { maxWidth, margin: "0 auto" } : undefined}
            >
                {logos.map((logo, index) => (
                    <li key={index}>
                        <Image
                            src={logo.src}
                            alt={logo.alt}
                            style={{
                                ...logoStyles,
                                ...(logo.width
                                    ? {
                                          width: `${logo.width}px`,
                                          height: "auto"
                                      }
                                    : {})
                            }}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );

    const TitleComponent = tagType ?? "h2";

    return isWacRefresh ? (
        title ? (
            <ColumnLayout
                layout="2-10"
                paddingTopv2="800"
                paddingBottomv2="800"
            >
                <TitleComponent
                    className={"h5"}
                    css={css({
                        margin: 0,
                        [media.below.md]: {
                            textAlign: "center"
                        }
                    })}
                >
                    {title}
                </TitleComponent>
                {logosComponent}
            </ColumnLayout>
        ) : (
            <ColumnLayout layout="12" paddingTopv2="800" paddingBottomv2="800">
                {logosComponent}
            </ColumnLayout>
        )
    ) : (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.600"),
                [media.above.md]: {
                    gap: token("space.800")
                },
                textAlign: "center"
            })}
        >
            {title && <h3 css={css({ margin: 0 })}>{title}</h3>}
            {logosComponent}
        </div>
    );
};

const logosWrapperStyle = css({
    width: "100%"
});

const logosContainerStyle = css({
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    margin: 0,
    padding: 0,
    listStyleType: "none",
    justifyContent: "center",
    rowGap: token("space.600"),
    columnGap: token("space.400"),
    [media.above.sm]: {
        columnGap: token("space.800")
    },
    [media.above.md]: {
        rowGap: token("space.800")
    },
    alignItems: "center"
});

const wacRefreshLogosContainerWithTitleStyles = css({
    justifyContent: "start",
    [media.below.md]: {
        justifyContent: "center"
    }
});

const wacRefreshContainerStyles = css({
    rowGap: token("space.400")
});
